/*
 * Piezas del proyecto que aparecen en varios sitios (tarjeta del rail, cabecera
 * del proyecto, pestaña Proyecto de la cuenta): estado del indicador, estado del
 * plan, stepper P1–P5 y la gráfica de lecturas contra la trayectoria necesaria.
 */

import { FASES_PROYECTO, type Proyecto } from "../api/tipos";
import { fmtFecha, hoyISO } from "./primitivas";

type P = Omit<Proyecto, "cuenta_id" | "cuenta">;

export function dias(a: string, b: string): number {
  return Math.round((new Date(b + "T12:00:00").getTime() - new Date(a + "T12:00:00").getTime()) / 86400000);
}
export function sumarDias(iso: string, n: number): string {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export type EstadoKpi = { id: string; label: string; color: string; bg: string };

/** En camino · En riesgo · Cumplido · No cumplido · Sin ventana. Una regla, un sitio. */
export function estadoKpi(p: P): EstadoKpi {
  const k = p.indicador, hoy = hoyISO();
  if (!k.ventana) return { id: "sin_ventana", label: "Sin ventana", color: "var(--faint)", bg: "var(--sunken)" };
  if (p.estado === "cerrado") return k.resultado === "cumplido" ? { id: "cumplido", label: "Cumplido", color: "var(--verified)", bg: "var(--verified-bg)" } : { id: "no_cumplido", label: "No cumplido", color: "var(--risk)", bg: "var(--risk-bg)" };
  if (dias(hoy, k.ventana.inicio) > 0) return { id: "sin_ventana", label: `Ventana abre ${fmtFecha(k.ventana.inicio)}`, color: "var(--muted)", bg: "var(--sunken)" };
  if (k.lecturas.length === 0) return { id: "sin_ventana", label: "Sin lecturas", color: "var(--muted)", bg: "var(--sunken)" };
  const last = k.lecturas[k.lecturas.length - 1];
  const total = dias(k.ventana.inicio, k.ventana.fin), t = dias(k.ventana.inicio, last.fecha) / total;
  const esperado = k.baseline + (k.meta - k.baseline) * t, tol = Math.abs(k.meta - k.baseline) * 0.05;
  const ok = k.direccion === "baja" ? last.valor <= esperado + tol : last.valor >= esperado - tol;
  return ok ? { id: "en_camino", label: "En camino", color: "var(--verified)", bg: "var(--verified-bg)" } : { id: "en_riesgo", label: "En riesgo", color: "var(--proposed)", bg: "var(--proposed-bg)" };
}

export function estadoPlan(p: P): { label: string; color: string; sub?: string } {
  if (p.estado === "cerrado") return { label: `Cerrado ${fmtFecha(p.cierre)}`, color: "var(--muted)" };
  if (p.estado === "por_arrancar") return { label: "Por arrancar", color: "var(--muted)" };
  const hoy = hoyISO(), pl = p.plan[p.fase];
  const bloqueadas = p.tareas.filter((t) => t.estado === "bloqueado");
  const retraso = pl && dias(pl.fin, hoy) > 0 ? dias(pl.fin, hoy) : 0;
  if (retraso > 0) return { label: `Retrasado ${retraso} d`, color: "var(--risk)", sub: bloqueadas[0]?.bloqueo ?? `${FASES_PROYECTO[p.fase].nombre} debía cerrar ${fmtFecha(pl.fin)}` };
  return { label: "A tiempo", color: "var(--verified)", sub: pl ? `Fase termina ${fmtFecha(pl.fin)}` : undefined };
}

export function unidad(k: P["indicador"]): string {
  return k.unidad === "%" ? "%" : k.unidad ? ` ${k.unidad}` : "";
}

export function PillKpi({ st }: { st: EstadoKpi }) {
  return <span className="pastilla" style={{ background: st.bg, color: st.color }}>{st.label}</span>;
}

export function StepperFases({ p, compact }: { p: P; compact?: boolean }) {
  const plan = estadoPlan(p);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${FASES_PROYECTO.length},1fr)`, gap: 4 }}>
      {FASES_PROYECTO.map((f, i) => {
        const done = i < p.fase || p.estado === "cerrado", cur = i === p.fase && p.estado === "activo";
        return (
          <div key={f.id} title={`${f.id} · ${f.nombre}`}>
            <div style={{ height: compact ? 4 : 5, borderRadius: 2, background: done ? "var(--navy)" : cur ? (plan.color === "var(--risk)" ? "var(--risk)" : "var(--sky)") : "var(--border)" }} />
            {!compact && <div style={{ marginTop: 6, fontSize: 11.5, color: cur ? "var(--text)" : "var(--faint)", fontWeight: cur ? 500 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.nombre}</div>}
          </div>
        );
      })}
    </div>
  );
}

/** Lecturas contra la recta baseline→meta sobre la ventana. La gris es la trayectoria necesaria. */
export function GraficaKpi({ p, h = 110, w = 320 }: { p: P; h?: number; w?: number }) {
  const k = p.indicador;
  if (!k.ventana) return null;
  const total = dias(k.ventana.inicio, k.ventana.fin) || 1;
  const lo = Math.min(k.baseline, k.meta), hi = Math.max(k.baseline, k.meta), pad = (hi - lo) * 0.25 || 1;
  const yMin = lo - pad, yMax = hi + pad;
  const X = (d: string) => 4 + (dias(k.ventana!.inicio, d) / total) * (w - 8);
  const Y = (v: number) => 4 + (1 - (v - yMin) / (yMax - yMin)) * (h - 8);
  const pts = k.lecturas.map((l) => `${X(l.fecha)},${Y(l.valor)}`).join(" ");
  const hoy = hoyISO(), inWin = dias(k.ventana.inicio, hoy) >= 0 && dias(hoy, k.ventana.fin) >= 0;
  const st = estadoKpi(p);
  const last = k.lecturas[k.lecturas.length - 1];
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: "block", overflow: "visible" }} role="img" aria-label={`${k.nombre}: ${last ? last.valor : "—"}${unidad(k)}`}>
      <line x1={4} x2={w - 4} y1={Y(k.meta)} y2={Y(k.meta)} stroke="var(--verified)" strokeDasharray="3 3" strokeWidth="1" />
      <line x1={X(k.ventana.inicio)} y1={Y(k.baseline)} x2={X(k.ventana.fin)} y2={Y(k.meta)} stroke="var(--border-strong)" strokeWidth="1.2" />
      {inWin && <line x1={X(hoy)} x2={X(hoy)} y1={4} y2={h - 4} stroke="var(--border)" strokeWidth="1" />}
      {pts && <polyline points={pts} fill="none" stroke={st.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
      {k.lecturas.map((l) => <circle key={l.fecha} cx={X(l.fecha)} cy={Y(l.valor)} r="3" fill="var(--card)" stroke={st.color} strokeWidth="2" />)}
      <text x={w - 4} y={Y(k.meta) - 5} textAnchor="end" fontSize="10" fill="var(--verified)" fontFamily="var(--font-mono)">meta {k.meta}{unidad(k)}</text>
    </svg>
  );
}

export function KpiGrande({ p, onClick }: { p: P; onClick?: () => void }) {
  const k = p.indicador, st = estadoKpi(p), last = k.lecturas[k.lecturas.length - 1];
  const val = last ? last.valor : k.baseline;
  const hoy = hoyISO();
  return (
    <button onClick={onClick} style={{ display: "flex", flexWrap: "wrap", gap: "12px 20px", alignItems: "flex-end", width: "100%", cursor: onClick ? "pointer" : "default" }}>
      <div style={{ flex: "0 0 auto" }}>
        <span className="tag11" style={{ color: "var(--faint)", display: "block", marginBottom: 2, whiteSpace: "nowrap" }}>{k.nombre}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 40, lineHeight: 1, color: st.color, letterSpacing: "-0.02em" }}>{val}{unidad(k)}</span>
        <span style={{ display: "block", fontSize: 12, color: "var(--muted)", marginTop: 6, whiteSpace: "nowrap" }}><span className="mono">{k.baseline}{unidad(k)}</span> baseline → <span className="mono">{k.meta}{unidad(k)}</span> meta{last ? ` · ${fmtFecha(last.fecha)}` : ""}</span>
      </div>
      <div style={{ flex: "1 1 220px", minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <PillKpi st={st} />
          {k.ventana && <span className="mono" style={{ color: "var(--faint)", fontSize: 11, whiteSpace: "nowrap" }}>día {Math.max(0, Math.min(dias(k.ventana.inicio, hoy) + 1, dias(k.ventana.inicio, k.ventana.fin) + 1))} de {dias(k.ventana.inicio, k.ventana.fin) + 1}</span>}
        </div>
        {k.ventana ? <GraficaKpi p={p} h={70} w={320} /> : <div style={{ height: 70, background: "var(--sunken)", borderRadius: "var(--r-control)", display: "grid", placeItems: "center", color: "var(--faint)", fontSize: 12 }}>La ventana de 30 días arranca con el piloto</div>}
      </div>
    </button>
  );
}
